import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { toast } from "react-toastify";
import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser } from "../../../lib/screenAccess";
import {
  controleProducaoService,
  PainelSobrasCelula,
  PainelSobrasItem,
  PainelSobrasResponse,
} from "../../../services/controleProducaoService";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";
import { currentMondayISO, mondayOfISO } from "../../../utils/controleProducaoDates";
import { addDaysISO } from "../../../utils/controleProducaoTurno";
import { BrazilDate } from "../../../utils/BrazilDate";

type LojaOpt = { value: number; label: string };
type OrigemFiltro = "todos" | "LOCAL" | "OUTRA_UNIDADE";

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

function dataCurtaBR(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function labelSemana(inicio: string, fim: string): string {
  return `${dataCurtaBR(inicio)} a ${dataCurtaBR(fim)}`;
}

function celulaDoDia(
  item: PainelSobrasItem,
  dataISO: string
): PainelSobrasCelula | undefined {
  return item.celulas.find((c) => c.dataISO === dataISO);
}

function textoCelula(celula: PainelSobrasCelula | undefined): string {
  if (!celula || celula.quantidade == null || celula.quantidade === "") {
    return "—";
  }
  const und = (celula.undNome ?? "").trim();
  return und ? `${celula.quantidade} ${und}` : celula.quantidade;
}

function normalizarBusca(texto: string): string {
  return texto
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

const ControleProducaoSobrasPage: React.FC = () => {
  const { user } = useAuth();
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [semanaInicio, setSemanaInicio] = useState(currentMondayISO);
  const [busca, setBusca] = useState("");
  const [origemFiltro, setOrigemFiltro] = useState<OrigemFiltro>("todos");
  const [loading, setLoading] = useState(false);
  const [painel, setPainel] = useState<PainelSobrasResponse | null>(null);

  const semanaFim = addDaysISO(semanaInicio, 6);
  const hojeISO = BrazilDate.todayISO();

  const lojaOptions = useMemo(
    () =>
      (podeVerTodasLojas
        ? lojas
        : lojas.filter((loja) => loja.id === user?.lojaId)
      ).map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas, podeVerTodasLojas, user?.lojaId]
  );

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  useEffect(() => {
    if (selectedLojaId != null) return;
    if (user?.lojaId != null && user.lojaId >= 1) {
      setSelectedLojaId(user.lojaId);
    }
  }, [user?.lojaId, selectedLojaId]);

  const carregar = useCallback(async () => {
    if (!selectedLojaId) return;
    setLoading(true);
    try {
      const data = await controleProducaoService.getPainelSobras({
        dataInicio: semanaInicio,
        dataFim: semanaFim,
        lojaId: selectedLojaId,
      });
      setPainel(data);
    } catch (error: { message?: string } | unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: string }).message)
          : "Não foi possível carregar as sobras.";
      toast.error(message);
      setPainel(null);
    } finally {
      setLoading(false);
    }
  }, [selectedLojaId, semanaInicio, semanaFim]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const itensFiltrados = useMemo(() => {
    const itens = painel?.itens ?? [];
    const termo = normalizarBusca(busca);
    return itens.filter((item) => {
      if (origemFiltro !== "todos" && item.origemProducao !== origemFiltro) {
        return false;
      }
      if (termo && !normalizarBusca(item.descricao).includes(termo)) {
        return false;
      }
      return true;
    });
  }, [painel?.itens, busca, origemFiltro]);

  const irSemana = (delta: number) => {
    setSemanaInicio(addDaysISO(semanaInicio, delta * 7));
  };

  return (
    <main className="px-3 py-4 sm:px-6">
      <PageHeader
        title="Sobras"
        description="Visualize a sobra lançada em cada dia da semana, inclusive nos dias sem produção e nos itens feitos em outra loja."
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(12rem,18rem)_auto_1fr]">
        {podeVerTodasLojas ? (
          <label className="min-w-0">
            <span className="mb-1.5 block text-sm font-medium text-neutral-700">
              Loja
            </span>
            <ReactSelect<LojaOpt, false>
              inputId="painel-sobras-loja"
              options={lojaOptions}
              value={
                lojaOptions.find((o) => o.value === selectedLojaId) ?? null
              }
              onChange={(opt: SingleValue<LojaOpt>) =>
                setSelectedLojaId(opt?.value)
              }
              styles={LOJA_SELECT_STYLES}
              placeholder="Selecione a loja"
              isSearchable={false}
            />
          </label>
        ) : null}

        <div className="flex min-w-0 flex-col justify-end">
          <span className="mb-1.5 block text-sm font-medium text-neutral-700">
            Semana
          </span>
          <div className="flex h-[38px] items-stretch overflow-hidden rounded-md border border-neutral-300 bg-white">
            <button
              type="button"
              onClick={() => irSemana(-1)}
              className="px-2.5 text-neutral-600 hover:bg-neutral-50"
              aria-label="Semana anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <p className="flex min-w-0 flex-1 items-center justify-center px-2 text-sm font-semibold tabular-nums text-neutral-800">
              {labelSemana(semanaInicio, semanaFim)}
            </p>
            <button
              type="button"
              onClick={() => irSemana(1)}
              className="px-2.5 text-neutral-600 hover:bg-neutral-50"
              aria-label="Próxima semana"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          {mondayOfISO(hojeISO) !== semanaInicio ? (
            <button
              type="button"
              onClick={() => setSemanaInicio(currentMondayISO())}
              className="h-[38px] rounded-md border border-neutral-300 bg-white px-3 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              Esta semana
            </button>
          ) : null}
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Buscar item</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
            aria-hidden
          />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar item…"
            className="h-10 w-full rounded-xl border border-neutral-300 py-2 pl-9 pr-3 text-sm"
            autoComplete="off"
          />
        </label>
        <div className="flex rounded-xl border border-neutral-200 bg-neutral-50 p-0.5">
          {(
            [
              ["todos", "Todos"],
              ["LOCAL", "Da loja"],
              ["OUTRA_UNIDADE", "Outra unidade"],
            ] as const
          ).map(([valor, label]) => (
            <button
              key={valor}
              type="button"
              onClick={() => setOrigemFiltro(valor)}
              className={`h-9 rounded-lg px-3 text-xs font-semibold ${
                origemFiltro === valor
                  ? "bg-white text-neutral-900 shadow-sm"
                  : "text-neutral-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm font-medium text-neutral-600">
            Carregando sobras…
          </p>
        </div>
      ) : !painel ? (
        <p className="py-10 text-center text-sm text-neutral-500">
          Selecione a loja para ver as sobras da semana.
        </p>
      ) : painel.dias.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-500">
          Nenhum dia aberto nesta semana.
        </p>
      ) : itensFiltrados.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-500">
          Nenhum item encontrado.
        </p>
      ) : (
        <>
          <div className="space-y-3 sm:hidden">
            {itensFiltrados.map((item) => (
              <article
                key={item.itemId}
                className="rounded-xl border border-neutral-200 bg-white p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold leading-snug text-neutral-900">
                    {item.descricao}
                  </h3>
                  {item.origemProducao === "OUTRA_UNIDADE" ? (
                    <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
                      Outra unidade
                    </span>
                  ) : null}
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-2">
                  {painel.dias.map((dia) => {
                    const celula = celulaDoDia(item, dia.dataISO);
                    const soSobra = dia.tipoDia === "sobra";
                    return (
                      <div
                        key={dia.dataISO}
                        className={`rounded-lg px-2 py-1.5 ${
                          soSobra ? "bg-amber-50" : "bg-neutral-50"
                        }`}
                        title={celula?.observacao ?? undefined}
                      >
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                          {dia.diaSemanaLabel.slice(0, 3)} {dataCurtaBR(dia.dataISO)}
                          {soSobra ? " · só sobra" : ""}
                        </dt>
                        <dd className="text-sm font-semibold tabular-nums text-neutral-900">
                          {textoCelula(celula)}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </article>
            ))}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-neutral-200 bg-white sm:block">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                  <th className="sticky left-0 z-10 min-w-[12rem] bg-neutral-50 px-3 py-2">
                    Item
                  </th>
                  {painel.dias.map((dia) => (
                    <th
                      key={dia.dataISO}
                      className={`min-w-[6.5rem] px-3 py-2 text-center ${
                        dia.tipoDia === "sobra" ? "bg-amber-50 text-amber-900" : ""
                      }`}
                    >
                      <span className="block">
                        {dia.diaSemanaLabel.slice(0, 3)}
                      </span>
                      <span className="block font-semibold tabular-nums normal-case tracking-normal text-neutral-700">
                        {dataCurtaBR(dia.dataISO)}
                      </span>
                      {dia.tipoDia === "sobra" ? (
                        <span className="mt-0.5 block text-[10px] font-semibold normal-case tracking-normal">
                          Só sobra
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itensFiltrados.map((item) => (
                  <tr
                    key={item.itemId}
                    className="border-t border-neutral-100 hover:bg-neutral-50/80"
                  >
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium leading-snug text-neutral-900">
                      <span className="block">{item.descricao}</span>
                      {item.origemProducao === "OUTRA_UNIDADE" ? (
                        <span className="mt-0.5 inline-block rounded-full bg-sky-100 px-2 py-px text-[10px] font-semibold text-sky-800">
                          Outra unidade
                        </span>
                      ) : null}
                    </td>
                    {painel.dias.map((dia) => {
                      const celula = celulaDoDia(item, dia.dataISO);
                      const vazio =
                        !celula ||
                        celula.quantidade == null ||
                        celula.quantidade === "";
                      return (
                        <td
                          key={dia.dataISO}
                          title={celula?.observacao ?? undefined}
                          className={`px-3 py-2 text-center tabular-nums ${
                            dia.tipoDia === "sobra" ? "bg-amber-50/60" : ""
                          } ${
                            vazio
                              ? "text-neutral-400"
                              : "font-semibold text-neutral-900"
                          }`}
                        >
                          {textoCelula(celula)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
};

export default ControleProducaoSobrasPage;
